import { useState, useEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useCollections } from "@/hooks/useCollections"
import { useAuthState } from "@/hooks/useAuth"
import { trackCollectionCreated, trackFirstCollectionCreated } from "@/lib/metaPixel"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Bookmark, Loader2 } from "lucide-react"
import type { PortfolioItem } from "@/types/models"
import { addDoc, collection, serverTimestamp, query, where, getDocs } from "firebase/firestore"
import { db } from "@/lib/firebase"
import { CollectionPreviewGrid } from "@/components/shared/CollectionPreviewGrid"
import { useAnalytics } from "@/hooks/useAnalytics"
import { Keyboard } from '@capacitor/keyboard'
import { getPlatform } from '@/lib/platform'

interface CollectionSelectSheetProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  item: PortfolioItem
  onSave: (selectedCollectionIds: string[]) => Promise<void>
}

export function CollectionSelectSheet({ 
  isOpen, 
  onOpenChange, 
  item,
  onSave 
}: CollectionSelectSheetProps) {
  const { t } = useTranslation()
  const { user } = useAuthState()
  const { trackEvent } = useAnalytics()
  const { collections, loading: collectionsLoading } = useCollections()
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [newCollectionTitle, setNewCollectionTitle] = useState("")
  const [isNewCollectionSelected, setIsNewCollectionSelected] = useState(false)
  const [keyboardHeight, setKeyboardHeight] = useState(0)

  // Keyboard tracking for iOS native - only run on iOS to avoid double keyboard handling
  useEffect(() => {
    const platform = getPlatform()
    if (platform !== 'ios') return

    let showListener: any
    let hideListener: any

    const setupKeyboardListeners = async () => {
      showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
        setKeyboardHeight(info.keyboardHeight)
      })
      hideListener = await Keyboard.addListener('keyboardWillHide', () => {
        setKeyboardHeight(0)
      })
    }

    void setupKeyboardListeners()

    return () => {
      void showListener?.remove()
      void hideListener?.remove()
    }
  }, [])

  // Initialize selected collections based on which collections contain this item
  useEffect(() => {
    if (!isOpen || collectionsLoading) return

    const collectionsContainingItem = collections.filter(collection => 
      collection.items.some(collectionItem => 
        collectionItem.id === item.id && 
        collectionItem.type === (item.isFlash ? 'flash' : 'portfolioItem')
      )
    )

    setSelectedCollectionIds(collectionsContainingItem.map(c => c.id))
  }, [isOpen, collections, collectionsLoading, item.id, item.isFlash])

  // Reset new collection input when sheet is closed
  useEffect(() => {
    if (!isOpen) {
      setNewCollectionTitle("")
      setIsNewCollectionSelected(false)
    }
  }, [isOpen])

  const handleCollectionToggle = useCallback((collectionId: string) => {
    setSelectedCollectionIds(prev => {
      if (prev.includes(collectionId)) {
        return prev.filter(id => id !== collectionId)
      } else {
        return [...prev, collectionId]
      }
    })
  }, [])

  const handleNewCollectionToggle = useCallback(() => {
    setIsNewCollectionSelected(prev => !prev)
  }, [])

  const handleNewCollectionTitleChange = useCallback((value: string) => {
    setNewCollectionTitle(value)
    // Auto-select when user starts typing
    if (value.trim() && !isNewCollectionSelected) {
      setIsNewCollectionSelected(true)
    }
  }, [isNewCollectionSelected])

  const handleApply = useCallback(async () => {
    setIsSaving(true)
    try {
      let finalCollectionIds = [...selectedCollectionIds]
      
      // Create new collection if it's selected and has a title
      if (isNewCollectionSelected && newCollectionTitle.trim() && user) {
        const collectionData = {
          userId: user.uid,
          title: newCollectionTitle.trim(),
          createdAt: serverTimestamp(),
          isAnonymous: false,
          items: [],
          itemCount: 0
        }
        
        const docRef = await addDoc(collection(db, 'collections'), collectionData)
        finalCollectionIds = [...finalCollectionIds, docRef.id]
        
        // Check if this is the user's first collection and track with Meta Pixel
        const collectionsRef = collection(db, 'collections')
        const userCollectionsQuery = query(
          collectionsRef,
          where('userId', '==', user.uid)
        )
        const existingCollections = await getDocs(userCollectionsQuery)
        const isFirstCollection = existingCollections.size === 1 // Just created, so size is 1

        trackEvent('collection_created', {
          collection_id: docRef.id,
          title: newCollectionTitle.trim(),
          is_first: isFirstCollection
        })
        
        if (isFirstCollection) {
          trackFirstCollectionCreated(newCollectionTitle.trim())
        } else {
          trackCollectionCreated(newCollectionTitle.trim())
        }
      }
      
      await onSave(finalCollectionIds)
      onOpenChange(false)
    } catch (error) {
      console.error('[CollectionSelectSheet] Error saving:', error)
    } finally {
      setIsSaving(false)
    }
  }, [selectedCollectionIds, isNewCollectionSelected, newCollectionTitle, user, onSave, onOpenChange, trackEvent])

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent 
        side="bottom" 
        className="h-auto max-h-[70vh] flex flex-col p-6" 
        hideCloseButton
        style={{
          bottom: keyboardHeight > 0 
            ? `calc((100vh - ${keyboardHeight}px - 70vh) / 2 + ${keyboardHeight}px)` 
            : 0,
          transition: 'bottom 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        } as React.CSSProperties}
      >
        <SheetHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <SheetTitle className="text-xl font-semibold flex items-center gap-2">
            <Bookmark className="w-5 h-5 shrink-0" aria-hidden />
            {t('saves.selectCollections', 'Save to Collections')}
          </SheetTitle>
        </SheetHeader>
        
        {collectionsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-0">
              {collections.map((collection) => {
                const isSelected = selectedCollectionIds.includes(collection.id)
                
                return (
                  <div 
                    key={collection.id} 
                    className="flex items-center py-4 border-b border-border/30"
                  >
                    <button
                      onClick={() => handleCollectionToggle(collection.id)}
                      className="w-full flex items-center gap-3 hover:bg-muted/50 rounded-lg p-2 -m-2 transition-colors"
                    >
                      <CollectionPreviewGrid collection={collection} size="sm" />
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-base font-medium truncate">
                          {collection.title}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {collection.itemCount || 0} {collection.itemCount === 1 ? 'item' : 'items'}
                        </p>
                      </div>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleCollectionToggle(collection.id)}
                        className="pointer-events-none flex-shrink-0"
                      />
                    </button>
                  </div>
                )
              })}
              
              {/* New Collection Input */}
              <div className="flex items-center py-4 border-b border-border/30">
                <div className="w-full flex items-center gap-3 pr-4">
                  <div className="flex-1 min-w-0 pt-0.5">
                    <Input
                      value={newCollectionTitle}
                      onChange={(e) => handleNewCollectionTitleChange(e.target.value)}
                      placeholder={t('saves.newCollectionPlaceholder', 'Enter new collection name...')}
                      disabled={isSaving}
                      className="h-auto border-0 p-0 rounded-none placeholder:text-muted-foreground focus-visible:ring-0"
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('saves.newCollectionSubtitle', 'New Collection')}
                    </p>
                  </div>
                  <Checkbox
                    checked={isNewCollectionSelected}
                    onCheckedChange={handleNewCollectionToggle}
                    disabled={isSaving}
                    className="flex-shrink-0 mt-0.5"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bottom Actions */}
        <div className="flex gap-3 pt-8 pb-6">
          <Button
            variant="outline"
            className="flex-1 border-border"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            className="flex-1"
            onClick={handleApply}
            disabled={
              isSaving || 
              collectionsLoading || 
              (selectedCollectionIds.length === 0 && !(isNewCollectionSelected && newCollectionTitle.trim()))
            }
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.saving', 'Saving...')}
              </>
            ) : (
              t('common.save', 'Save')
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
